// src/pages/ScenariosPage.js - Interactive scenarios page component
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import { motion } from 'framer-motion';
import QuizComponent from '../components/interactive/QuizComponent';
import PHIChecklistComponent from '../components/interactive/PHIChecklistComponent';
import EmailSimulator, { EMAIL_SIMULATOR_INSTRUCTION, EmailEncryptionMethods } from '../components/interactive/EmailSimulator';
import RecipientExperienceComponent from '../components/interactive/RecipientExperienceComponent';
import { useLocation } from 'react-router-dom';

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--spacing-lg);
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  margin: var(--spacing-xl) 0 var(--spacing-lg);
`;

const ScenariosContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-xxl);
`;

const IntroText = styled.p`
  font-size: 1.125rem;
  margin-bottom: var(--spacing-xl);
  max-width: 800px;
`;

const ScenarioCard = styled(Card)`
  height: 100%;
  
  .card-content {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  
  .card-footer {
    margin-top: auto;
    padding-top: var(--spacing-md);
  }
`;

const ScenarioModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-md);
`;

const ModalContent = styled(motion.div)`
  background-color: var(--background-color);
  border-radius: var(--border-radius);
  padding: var(--spacing-xl);
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  margin-bottom: var(--spacing-lg);
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: var(--spacing-sm);
`;

const CloseButton = styled.button`
  position: absolute;
  top: var(--spacing-md);
  right: var(--spacing-md);
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-color);
`;

const ScenarioDescription = styled.div`
  margin-bottom: var(--spacing-xl);
  line-height: 1.6;
`;

const ScenarioOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-xl);
`;

const ScenarioOption = styled.button`
  background-color: var(--background-light);
  border: 1px solid var(--background-dark);
  border-radius: var(--border-radius);
  padding: var(--spacing-md);
  text-align: left;
  font-family: var(--font-family);
  font-size: 1rem;
  cursor: pointer;
  transition: background-color var(--transition-speed) ease;
  
  &:hover {
    background-color: var(--background-dark);
  }
  
  &:focus {
    outline: 2px solid var(--primary-color);
    outline-offset: 1px;
  }
`;

const FeedbackContainer = styled.div`
  padding: var(--spacing-md);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-xl);
  background-color: ${props => 
    props.type === 'correct' ? 'rgba(16, 124, 16, 0.1)' : 
    props.type === 'incorrect' ? 'rgba(209, 52, 56, 0.1)' : 
    'rgba(255, 140, 0, 0.1)'
  };
  border-left: 4px solid ${props => 
    props.type === 'correct' ? 'var(--success-color)' : 
    props.type === 'incorrect' ? 'var(--error-color)' : 
    'var(--warning-color)'
  };
`;

const TabContainer = styled.div`
  margin-bottom: var(--spacing-xl);
`;

const TabButtons = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
  border-bottom: 1px solid var(--background-dark);
`;

const TabButton = styled.button`
  background: none;
  border: none;
  padding: var(--spacing-md) var(--spacing-lg);
  font-family: var(--font-family);
  font-size: 1rem;
  cursor: pointer;
  position: relative;
  color: ${props => props.active ? 'var(--primary-color)' : 'var(--text-color)'};
  font-weight: ${props => props.active ? '600' : '400'};
  
  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 3px;
    background-color: var(--primary-color);
    opacity: ${props => props.active ? '1' : '0'};
    transition: opacity var(--transition-speed) ease;
  }
  
  &:hover {
    color: var(--primary-color);
  }
`;

const TabContent = styled.div`
  padding: var(--spacing-md) 0;
`;

const Instructions = styled.div`
  background-color: var(--background-light);
  padding: var(--spacing-md);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-md);
`;

const VideoContainer = styled.div`
  margin-bottom: var(--spacing-xl);
`;

const VideoWrapper = styled.div`
  margin-bottom: var(--spacing-lg);
  border: 1px solid var(--background-dark);
  border-radius: var(--border-radius);
  overflow: hidden;
`;

const VideoFrame = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  
  iframe, video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const VideoTitle = styled.h3`
  font-size: 1.25rem;
  margin: var(--spacing-md) 0;
`;

const VideoDescription = styled.p`
  margin-bottom: var(--spacing-md);
`;

const VideoSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
`;

const VideoThumbnail = styled.div`
  width: 200px;
  cursor: pointer;
  border: 2px solid ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  border-radius: var(--border-radius);
  overflow: hidden;
  transition: transform var(--transition-speed) ease;
  
  &:hover {
    transform: translateY(-3px);
  }
  
  img {
    width: 100%;
    height: 120px;
    object-fit: cover;
  }
  
  p {
    padding: var(--spacing-sm);
    font-size: 0.875rem;
    background-color: var(--background-light);
  }
`;

// Sample scenarios
const scenarios = [
  {
    id: 1,
    title: 'Urgent Pharmacogenomic Result Request',
    description: 'A physician calls requesting pharmacogenomic test results for a patient who needs to start a new medication immediately.',
    shortDescription: 'Handle an urgent request for Youscript PGX results needed for immediate medication decision.',
    image: null,
    steps: [
      {
        description: "Dr. Johnson calls and says, \"I need my patient's Youscript test results right away. We need to start them on an antidepressant today, and I want to check for potential gene-drug interactions before prescribing. Can you email the results to me now?\"",
        options: [
          {
            text: "Email the report using regular email since this is an urgent clinical situation.",
            feedback: "Incorrect. Even in urgent clinical situations, PHI must be protected. The urgency doesn't override security requirements for patient genetic information.",
            isCorrect: false,
            next: "end-incorrect"
          },
          {
            text: "Tell the physician you cannot provide results via email for privacy reasons and they'll need to log into the portal.",
            feedback: "Partially correct. While you're right to be concerned about privacy, there are secure ways to handle this that meet both security requirements and the urgent clinical need.",
            isCorrect: false,
            next: "end-partial"
          },
          {
            text: "Verify the physician's identity, then offer to send the results via encrypted email or direct them to access results immediately through the secure 2bPrecise portal.",
            feedback: "Correct! This approach maintains security while responding to the urgent clinical need. Using encryption or the secure portal ensures PHI protection while providing the time-sensitive pharmacogenomic data needed for clinical decision-making.",
            isCorrect: true,
            next: "end-correct"
          }
        ]
      },
      {
        id: "end-incorrect",
        description: "The physician receives the unencrypted email containing the patient's pharmacogenomic test results. While this solved the immediate clinical need, it put sensitive genetic information at risk of unauthorized access. This represents a HIPAA violation that could result in penalties for Aranscia.",
        options: []
      },
      {
        id: "end-partial",
        description: "The physician becomes frustrated as they cannot quickly access the pharmacogenomic data needed to make a safe medication decision. While you protected privacy, the situation could have been handled in a way that met both security requirements and patient care needs.",
        options: []
      },
      {
        id: "end-correct",
        description: "You securely provide the YouScript results. The physician can immediately review gene-drug interactions for the proposed medication and select an appropriate alternative with reduced risk of adverse events. The PHI remains protected, and your organization maintains HIPAA compliance while supporting optimal patient care.",
        options: []
      }
    ]
  },
  {
    id: 2,
    title: 'External Laboratory Collaboration',
    description: 'You need to collaborate with an external laboratory on a complex pharmacogenomic case, which requires sharing test results and the patient\'s medication history.',
    shortDescription: 'Share YouScript test data with an external lab partner while maintaining PHI security.',
    image: null,
    steps: [
      {
        description: "A complex case requires consultation with specialists at a partner reference laboratory. They need to see the patient's YouScript test results and current medication list to help interpret unusual drug-gene interactions. How should you share this information?",
        options: [
          {
            text: "Email the YouScript report and medication list as PDF attachments, asking them to confirm receipt.",
            feedback: "Incorrect. Regular email attachments are not secure for transmitting pharmacogenomic data and medication information, even with a confirmation request. This method risks unauthorized access to sensitive genetic information.",
            isCorrect: false,
            next: "ext-end-incorrect"
          },
          {
            text: "Use Aranscia's secure file sharing system that requires recipient authentication and maintains an audit trail of access.",
            feedback: "Correct! A secure file sharing system with authentication ensures only the authorized laboratory specialists can access the sensitive genetic and medication information while providing an audit trail of the access.",
            isCorrect: true,
            next: "ext-end-correct"
          },
          {
            text: "Share the case details through a quick screenshare in an impromptu video call since it's with a trusted partner lab.",
            feedback: "Incorrect. Impromptu screensharing of PHI without proper documentation or security protocols violates privacy guidelines, even with trusted partners. There's no way to control what might be captured or recorded during such sessions.",
            isCorrect: false,
            next: "ext-end-incorrect"
          }
        ]
      },
      {
        id: "ext-end-incorrect",
        description: "The patient's sensitive pharmacogenomic data was transmitted through an unsecured channel. This creates significant security risks for this highly sensitive genetic information and represents a potential HIPAA violation that could lead to penalties, reputational damage, and compromise of patient trust.",
        options: []
      },
      {
        id: "ext-end-correct",
        description: "The reference laboratory specialists securely receive the YouScript results and medication list through the authenticated system. The collaboration proceeds effectively while maintaining patient privacy. The secure system also maintains a complete audit trail of who accessed this sensitive genetic information.",
        options: []
      }
    ]
  },
  {
    id: 3,
    title: 'Misdirected Lab Report',
    description: 'You receive an email containing a 2bPrecise order and patient information that appears to have been sent to you by mistake. The email contains sensitive patient data meant for another healthcare provider.',
    shortDescription: 'Handle a misdirected email containing lab order information from the 2bPrecise system.',
    image: null,
    steps: [
      {
        description: "You open your work email and find a message containing a patient's 2bPrecise lab order and diagnostic information. After reading it, you realize it was meant for a different lab specialist, not you. What should you do?",
        options: [
          {
            text: "Forward the email to the correct specialist with a note explaining the routing mistake.",
            feedback: "Incorrect. Forwarding the email would perpetuate the privacy breach by creating another copy of the PHI in another inbox. This compounds the original error in the 2bPrecise workflow.",
            isCorrect: false,
            next: "mis-end-incorrect"
          },
          {
            text: "Delete the email immediately to protect the patient's diagnostic information.",
            feedback: "Partially correct. While deleting helps contain the breach, simply deleting without reporting doesn't address the workflow issue in the 2bPrecise system or help prevent future incidents.",
            isCorrect: false,
            next: "mis-end-partial"
          },
          {
            text: "Reply only to the sender, inform them of the mistake, delete the email, and report the incident to improve the 2bPrecise workflow.",
            feedback: "Correct! This approach properly contains the breach, notifies the sender so they can take appropriate action, and follows proper incident reporting protocols which can help identify any needed improvements in the order routing process.",
            isCorrect: true,
            next: "mis-end-correct"
          }
        ]
      },
      {
        id: "mis-end-incorrect",
        description: "By forwarding the email with the 2bPrecise order, you've now created multiple instances of the PHI breach, complicating containment efforts and expanding the potential compromise. This would be considered a secondary breach that compounds the original privacy violation.",
        options: []
      },
      {
        id: "mis-end-partial",
        description: "While the immediate breach is contained by deletion, the sender remains unaware of their mistake and may continue to make the same error in the 2bPrecise system. Without proper reporting, the organization can't track or address potential workflow issues that might be causing order misdirection.",
        options: []
      },
      {
        id: "mis-end-correct",
        description: "Your response contains the breach, notifies the sender so they can correct the order routing, and ensures proper incident documentation. The organization can now implement corrective measures in the 2bPrecise workflow to prevent similar incidents in the future.",
        options: []
      }
    ]
  },
  {
    id: 4,
    title: 'Remote Work Situation',
    description: "You're working remotely from a coffee shop and need to respond to an urgent request about a patient's pharmacogenomic test results.",
    shortDescription: 'Manage pharmacogenomic data securely while working from a public location.',
    image: null,
    steps: [
      {
        description: "While working at a coffee shop, you receive an urgent message from a clinician asking for clarification on a specific gene variant in a patient's YouScript pharmacogenomic report that could impact their medication choice. How should you handle this situation?",
        options: [
          {
            text: "Open the YouScript report and respond immediately with the requested genetic information since it's clinically urgent.",
            feedback: "Incorrect. Working in a public location creates additional security risks for accessing sensitive genetic information that must be addressed regardless of clinical urgency.",
            isCorrect: false,
            next: "rem-end-incorrect"
          },
          {
            text: "Tell the clinician you'll respond when you return to the office to ensure genetic data security.",
            feedback: "Partially correct. While this ensures security, it doesn't balance the need for timely clinical decision-making that could affect patient safety. There are secure ways to respond remotely.",
            isCorrect: false,
            next: "rem-end-partial"
          },
          {
            text: "Move to a private location, use a privacy screen, connect to a secure VPN, and then access the YouScript system to respond using encrypted communication.",
            feedback: "Correct! This approach addresses both the security concerns of working with genetic data in public and the need for a timely response to urgent clinical pharmacogenomic questions.",
            isCorrect: true,
            next: "rem-end-correct"
          }
        ]
      },
      {
        id: "rem-end-incorrect",
        description: "By accessing and discussing genetic information in a public location, you've created a situation where unauthorized individuals could potentially view sensitive pharmacogenomic data on your screen. Additionally, using an unsecured public network increases the risk of data interception.",
        options: []
      },
      {
        id: "rem-end-partial",
        description: "While security of the genetic information is maintained, the delay in response could potentially impact the clinician's medication decision and patient safety. In pharmacogenomics, we need to balance security requirements with the need for timely clinical guidance.",
        options: []
      },
      {
        id: "rem-end-correct",
        description: "Your approach successfully balances security and clinical needs. By taking appropriate precautions—moving to a private location, using a privacy screen, connecting through VPN, and using encrypted communication—you've maintained the security of sensitive genetic information while providing timely guidance for medication selection.",
        options: []
      }
    ]
  },
  {
    id: 5,
    title: 'Internal PHI Communication',
    description: 'You need to share diagnostic test results across several Aranscia internal teams to coordinate complex test interpretation and reporting.',
    shortDescription: 'Securely communicate PHI between laboratory and clinical teams within Aranscia.',
    image: null,
    steps: [
      {
        description: "A complex pharmacogenomic test case requires input from the AccessDx lab team, the YouScript clinical interpretation team, and the 2bPrecise workflow specialists to ensure proper results delivery to the ordering provider. How should you coordinate sharing the necessary patient information across teams?",
        options: [
          {
            text: "Create a group email with all departments and attach the patient's full test results and medical history.",
            feedback: "Incorrect. This approach shares more information than necessary with each team and creates unnecessary copies of sensitive genetic information, violating the minimum necessary standard.",
            isCorrect: false,
            next: "int-end-incorrect"
          },
          {
            text: "Use Aranscia's secure messaging system to send only relevant components of the case to each team based on their specific role in the workflow.",
            feedback: "Correct! This follows the minimum necessary principle by sharing only relevant information with each team through secure, approved channels, while maintaining the integrity of the diagnostic workflow.",
            isCorrect: true,
            next: "int-end-correct"
          },
          {
            text: "Print the relevant sections of the test results and deliver them to each team personally.",
            feedback: "Partially correct. While this limits electronic transmission risks, it creates physical documents containing sensitive genetic information that need secure handling and proper disposal, and may delay critical test interpretation and reporting.",
            isCorrect: false,
            next: "int-end-partial"
          }
        ]
      },
      {
        id: "int-end-incorrect",
        description: "Your approach has shared excessive genetic and clinical data with multiple recipients, many of whom received sensitive information they don't need for their specific role in the testing workflow. This violates the HIPAA minimum necessary standard and increases the risk of unauthorized access to sensitive pharmacogenomic data.",
        options: []
      },
      {
        id: "int-end-partial",
        description: "While you've avoided electronic transmission risks, you've created multiple physical copies of sensitive genetic information that now require secure handling and eventual secure disposal. This approach also takes more time and may delay proper test interpretation and reporting for clinical care.",
        options: []
      },
      {
        id: "int-end-correct",
        description: "Each Aranscia team receives only the components of the case they need through secure channels, following both security best practices and the HIPAA minimum necessary principle. The pharmacogenomic test interpretation and reporting process proceeds efficiently while maintaining appropriate protections for sensitive genetic information.",
        options: []
      }
    ]
  },
  {
    id: 6,
    title: 'Billing Data Sharing',
    description: 'The Accounts Payable department needs to share billing information with a client organization. You need to determine the appropriate method based on the content.',
    shortDescription: 'Determine how to securely share billing information with clients based on PHI content.',
    image: null,
    steps: [
      {
        description: "Your manager in the AP department asks you to send a monthly billing report to Memorial Health System. The standard report contains service dates, test codes, and total charges but no patient identifiers. However, they've requested an itemized version with patient names and DOBs for reconciliation purposes. How should you proceed?",
        options: [
          {
            text: "Send both the standard report and the itemized version via regular email since it's just routine billing information.",
            feedback: "Incorrect. While the standard report without patient identifiers can be sent via regular email, the itemized version with patient names and DOBs contains PHI and requires secure transmission.",
            isCorrect: false,
            next: "bill-end-incorrect"
          },
          {
            text: "Send the standard report (no PHI) via regular email, but send the itemized version (with PHI) via encrypted email.",
            feedback: "Correct! This approach appropriately distinguishes between non-PHI data that can be sent via regular email and PHI-containing data that requires encryption. You're applying the right security level based on the content.",
            isCorrect: true,
            next: "bill-end-correct"
          },
          {
            text: "Encrypt both reports since they both contain sensitive billing information, even if one doesn't technically contain PHI.",
            feedback: "Partially correct. While this approach is secure, it applies unnecessary encryption to the standard report that contains no PHI, potentially creating workflow inefficiencies. Security measures should be appropriate to the type of data being shared.",
            isCorrect: false,
            next: "bill-end-partial"
          }
        ]
      },
      {
        id: "bill-end-incorrect",
        description: "By sending the itemized report with patient names and DOBs via regular email, you've transmitted PHI through an unsecured channel. This represents a HIPAA violation that could result in penalties for Aranscia, even though it was for legitimate billing purposes.",
        options: []
      },
      {
        id: "bill-end-partial",
        description: "Your approach is secure but applies encryption unnecessarily to non-PHI data. While this doesn't create a security issue, it may create unnecessary friction in business operations when sharing routine non-PHI billing information.",
        options: []
      },
      {
        id: "bill-end-correct",
        description: "You've appropriately applied different security standards based on the content of each report. The standard report without PHI was shared efficiently via regular email, while the sensitive patient information in the itemized report was properly protected through encryption.",
        options: []
      }
    ]
  }
];

// Sample videos data
const videos = [
  {
    id: 1,
    title: "Outlook Email Encryption Guide",
    description: "Step-by-step demonstration of how to properly encrypt emails containing PHI in Microsoft Outlook.",
    type: "custom",
    url: "/videos/outlook-encryption.mp4",
    thumbnail: "/videos/images/thumbnails/outlook-encryption.jpg"
  },
  {
    id: 2,
    title: "Opening Encrypted Emails Walkthrough",
    description: "Learn how to securely open and view encrypted emails containing protected health information.",
    type: "custom",
    url: "/videos/open-encrypted-email.mp4",
    thumbnail: "/videos/images/thumbnails/open-encrypted-email.jpg"
  },
  {
    id: 3,
    title: "PHI Handling Best Practices",
    description: "This video covers the essential best practices for handling PHI.",
    type: "youtube",
    url: "https://www.youtube.com/embed/GlviKry15mA?si=bwflswOcTh0XMaTM", // video url, needs to be embedded
    thumbnail: "/videos/images/thumbnails/PHI.png" //  thumbnail
  }
];

// Video component
const VideoPlayer = ({ video }) => {
  if (!video) return null;
  
  return (
    <VideoContainer>
      <VideoTitle>{video.title}</VideoTitle>
      <VideoDescription>{video.description}</VideoDescription>
      <VideoWrapper>
        <VideoFrame>
          {video.type === 'youtube' ? (
            <iframe 
              src={`${video.url}${video.url.includes('?') ? '&' : '?'}autoplay=1`}
              title={video.title}
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowFullScreen
            ></iframe>
          ) : (
            <video 
              key={video.id} 
              controls 
              autoPlay
            >
              <source src={video.url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}
        </VideoFrame>
      </VideoWrapper>
    </VideoContainer>
  );
};

const ScenariosPage = () => {
  const location = useLocation();
  const [activeScenario, setActiveScenario] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [activeTab, setActiveTab] = useState('scenarios');
  const [method, setMethod] = useState(1); // 1 for manual encryption, 2 for keyword option
  const [selectedVideo, setSelectedVideo] = useState(videos[0]);
  
  // Handle tab parameter from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam && ['scenarios', 'quiz', 'simulator', 'recipient', 'checklist', 'videos'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location]);
  
  const openScenario = (scenario) => {
    setActiveScenario(scenario);
    setCurrentStep(0);
    setSelectedOption(null);
  };
  
  const closeScenario = () => {
    setActiveScenario(null);
    setCurrentStep(0);
    setSelectedOption(null);
  };
  
  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    
    if (option.next) {
      // Find the next step based on the option's next property
      const nextStep = activeScenario.steps.findIndex(step => step.id === option.next);
      if (nextStep !== -1) {
        setCurrentStep(nextStep);
      }
    }
  };
  
  const resetScenario = () => {
    setCurrentStep(0);
    setSelectedOption(null);
  };
  
  return (
    <>
      <PageTitle>Learning & Assessment</PageTitle>
      
      <IntroText>
        Test your knowledge and practice your skills through interactive scenarios, quizzes, 
        practical email simulations, training videos, and reference materials. Use the printable checklist 
        as a reference guide for handling PHI in emails.
      </IntroText>
      
      <TabContainer>
        <TabButtons>
          <TabButton 
            active={activeTab === 'scenarios'} 
            onClick={() => setActiveTab('scenarios')}
          >
            Interactive Scenarios
          </TabButton>
          <TabButton 
            active={activeTab === 'quiz'} 
            onClick={() => setActiveTab('quiz')}
          >
            Knowledge Quiz
          </TabButton>
          <TabButton 
            active={activeTab === 'simulator'} 
            onClick={() => setActiveTab('simulator')}
          >
            Email Simulator
          </TabButton>
          <TabButton 
            active={activeTab === 'recipient'} 
            onClick={() => setActiveTab('recipient')}
          >
            Recipient Experience
          </TabButton>
          <TabButton 
            active={activeTab === 'checklist'} 
            onClick={() => setActiveTab('checklist')}
          >
            PHI Checklist
          </TabButton>
          <TabButton 
            active={activeTab === 'videos'} 
            onClick={() => setActiveTab('videos')}
          >
            Training Videos
          </TabButton>
        </TabButtons>
        
        <TabContent>
          {activeTab === 'scenarios' && (
            <>
              <SectionTitle>Choose a Scenario</SectionTitle>
              
              <ScenariosContainer>
                {scenarios.map(scenario => (
                  <ScenarioCard 
                    key={scenario.id}
                    title={scenario.title}
                    description={scenario.shortDescription}
                    interactive
                    onClick={() => openScenario(scenario)}
                  >
                    <div className="card-content">
                      <div className="card-footer">
                        <Button onClick={() => openScenario(scenario)}>Start Scenario</Button>
                      </div>
                    </div>
                  </ScenarioCard>
                ))}
              </ScenariosContainer>
            </>
          )}
          
          {activeTab === 'quiz' && <QuizComponent />}
          
          {activeTab === 'simulator' && (
            <>
              <Instructions>
                <strong>Instructions:</strong> {EMAIL_SIMULATOR_INSTRUCTION}
              </Instructions>
              <EmailEncryptionMethods method={method} setMethod={setMethod} />
              <EmailSimulator method={method} setMethod={setMethod} />
            </>
          )}
          
          {activeTab === 'recipient' && <RecipientExperienceComponent />}
          
          {activeTab === 'checklist' && <PHIChecklistComponent />}
          
          {activeTab === 'videos' && (
            <>
              <SectionTitle>Training Videos</SectionTitle>
              
              <VideoSelector>
                {videos.map(video => (
                  <VideoThumbnail 
                    key={video.id} 
                    active={selectedVideo && selectedVideo.id === video.id}
                    onClick={() => setSelectedVideo(video)}
                  >
                    <img src={video.thumbnail} alt={video.title} />
                    <p>{video.title}</p>
                  </VideoThumbnail>
                ))}
              </VideoSelector>
              
              <VideoPlayer video={selectedVideo} />
            </>
          )}
        </TabContent>
      </TabContainer>
      
      {activeScenario && (
        <ScenarioModal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <ModalContent
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            <CloseButton onClick={closeScenario}>&times;</CloseButton>
            
            <ModalHeader>
              <ModalTitle>{activeScenario.title}</ModalTitle>
              <p>{activeScenario.description}</p>
            </ModalHeader>
            
            {activeScenario.steps[currentStep] && (
              <>
                <ScenarioDescription>
                  {activeScenario.steps[currentStep].description}
                </ScenarioDescription>
                
                {activeScenario.steps[currentStep].options && activeScenario.steps[currentStep].options.length > 0 ? (
                  <>
                    {!selectedOption && (
                      <ScenarioOptions>
                        {activeScenario.steps[currentStep].options.map((option, index) => (
                          <ScenarioOption 
                            key={index}
                            onClick={() => handleOptionSelect(option)}
                          >
                            {option.text}
                          </ScenarioOption>
                        ))}
                      </ScenarioOptions>
                    )}
                    
                    {selectedOption && (
                      <FeedbackContainer type={selectedOption.isCorrect ? 'correct' : 'incorrect'}>
                        <h4>{selectedOption.isCorrect ? '✅ Correct!' : '❌ Incorrect'}</h4>
                        <p>{selectedOption.feedback}</p>
                      </FeedbackContainer>
                    )}
                  </>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--spacing-xl)' }}>
                    <Button onClick={resetScenario}>Try Again</Button>
                    <Button variant="secondary" onClick={closeScenario} style={{ marginLeft: 'var(--spacing-md)' }}>
                      Exit Scenario
                    </Button>
                  </div>
                )}
              </>
            )}
          </ModalContent>
        </ScenarioModal>
      )}
    </>
  );
};

export default ScenariosPage; 